import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Table, Button, Spinner } from 'reactstrap';
import { getPages, getUserPermissions, saveUserPermissions } from '../Api/GmsPermission';
import Swal from 'sweetalert2';
import { getSidebarName } from './PageNameMapper';

const PermissionManager = ({ userId, username }) => {
  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [masterChecked, setMasterChecked] = useState(false);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pagesData, userPerms] = await Promise.all([
        getPages(),
        getUserPermissions(userId)
      ]);

      // Create a hierarchical structure of pages
      const hierarchicalPages = buildPageHierarchy(pagesData);
      setPages(hierarchicalPages);

      // Initialize permissions
      const initialPermissions = pagesData.map(page => {
        const existingPerm = userPerms.find(p => p.permission_Page_Id === page.page_Id);
        return {
          permission_User_Id: userId,
          permission_Page_Id: page.page_Id,
          permission_View: Boolean(existingPerm?.permission_View ?? false),
          permission_Edit: Boolean(existingPerm?.permission_Edit ?? false),
          permission_Delete: Boolean(existingPerm?.permission_Delete ?? false),
          permission_Approve: Number(existingPerm?.permission_Approve ?? 0)
        };
      });

      setPermissions(initialPermissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to load permissions',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const buildPageHierarchy = (flatPages) => {
    const pageMap = {};
    const rootPages = [];

    // First pass: Create a map of all pages
    flatPages.forEach(page => {
      pageMap[page.page_Id] = {
        ...page,
        children: []
      };
    });

    // Second pass: Build the hierarchy
    flatPages.forEach(page => {
      if (page.page_Parent_Page_Id) {
        const parent = pageMap[page.page_Parent_Page_Id];
        if (parent) {
          parent.children.push(pageMap[page.page_Id]);
        }
      } else {
        rootPages.push(pageMap[page.page_Id]);
      }
    });

    // Sort pages alphabetically by page_NameEn
    const sortPages = (pages) => {
      return pages.sort((a, b) => a.page_NameEn.localeCompare(b.page_NameEn));
    };

    // Sort root pages and their children recursively
    const sortHierarchy = (pages) => {
      const sorted = sortPages(pages);
      sorted.forEach(page => {
        if (page.children && page.children.length > 0) {
          page.children = sortHierarchy(page.children);
        }
      });
      return sorted;
    };

    return sortHierarchy(rootPages);
  };

  const handlePermissionChange = (pageId, field, value) => {
    setPermissions(prev => {
      const updated = prev.map(p => 
        p.permission_Page_Id === pageId
          ? { 
              ...p, 
              [field]: field === 'permission_Approve' ? Number(value) : Boolean(value),
              // Auto-select View when Edit is selected
              ...(field === 'permission_Edit' && value ? { permission_View: true } : {}),
              // Auto-select View and Edit when Delete is selected
              ...(field === 'permission_Delete' && value ? { permission_View: true, permission_Edit: true } : {}),
              // Auto-select all permissions when Approve is selected
              ...(field === 'permission_Approve' && value ? { permission_View: true, permission_Edit: true, permission_Delete: true } : {})
            }
          : p
      );
      
      // Update master checkbox state based on all permissions
      const allFullyChecked = updated.every(p => 
        p.permission_View && p.permission_Edit && p.permission_Delete && p.permission_Approve > 0
      );
      setMasterChecked(allFullyChecked);
      
      return updated;
    });
  };

  const handleRowLevelChange = (pageId, checked) => {
    setPermissions(prev => {
      const updated = prev.map(p => 
        p.permission_Page_Id === pageId
          ? {
              ...p,
              permission_View: Boolean(checked),
              permission_Edit: Boolean(checked),
              permission_Delete: Boolean(checked),
              permission_Approve: Number(checked ? 1 : 0)
            }
          : p
      );
      
      // Update master checkbox state based on all permissions
      const allFullyChecked = updated.every(p => 
        p.permission_View && p.permission_Edit && p.permission_Delete && p.permission_Approve > 0
      );
      setMasterChecked(allFullyChecked);
      
      return updated;
    });
  };

  const handleMasterCheckboxChange = (checked) => {
    setMasterChecked(checked);
    setPermissions(prev => prev.map(p => ({
      ...p,
      permission_View: Boolean(checked),
      permission_Edit: Boolean(checked),
      permission_Delete: Boolean(checked),
      permission_Approve: Number(checked ? 1 : 0)
    })));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Filter out permissions where nothing is granted and ensure proper data types
      const permissionsToSave = permissions.filter(p => 
        p.permission_View || 
        p.permission_Edit || 
        p.permission_Delete || 
        p.permission_Approve > 0
      ).map(p => ({
        permission_User_Id: p.permission_User_Id,
        permission_Page_Id: p.permission_Page_Id,
        permission_View: Boolean(p.permission_View),
        permission_Edit: Boolean(p.permission_Edit),
        permission_Delete: Boolean(p.permission_Delete),
        permission_Approve: Number(p.permission_Approve) || 0
      }));

      console.log('Filtered permissions to save:', permissionsToSave);

      await saveUserPermissions({
        user_Id: userId,
        permissions: permissionsToSave
      });

      Swal.fire({
        title: 'Success!',
        text: 'Permissions saved successfully',
        icon: 'success'
      });
    } catch (error) {
      console.error('Error saving permissions:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to save permissions',
        icon: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const isRowFullyChecked = (permission) => {
    return permission.permission_View &&
           permission.permission_Edit &&
           permission.permission_Delete &&
           permission.permission_Approve > 0;
  };

  const renderPageRows = (pages, level = 0) => {
    return pages.flatMap(page => {
      const currentPermission = permissions.find(p => p.permission_Page_Id === page.page_Id);
      // Use sidebar name if available, otherwise fall back to database name
      const displayName = getSidebarName(page.page_Id, page.page_NameEn);
      const rows = [
        <tr key={page.page_Id}>
          <td style={{ paddingLeft: `${level * 20}px` }}>
            {displayName}
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              checked={isRowFullyChecked(currentPermission)}
              onChange={(e) => handleRowLevelChange(page.page_Id, e.target.checked)}
              className="row-master-checkbox"
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              checked={currentPermission?.permission_View || false}
              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_View', e.target.checked)}
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              checked={currentPermission?.permission_Edit || false}
              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Edit', e.target.checked)}
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              checked={currentPermission?.permission_Delete || false}
              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Delete', e.target.checked)}
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              checked={currentPermission?.permission_Approve || false}
              onChange={(e) => handlePermissionChange(page.page_Id, 'permission_Approve', e.target.checked)}
            />
          </td>
        </tr>
      ];

      if (page.children?.length > 0) {
        rows.push(...renderPageRows(page.children, level + 1));
      }

      return rows;
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <CardTitle className="mb-0">
            Manage Permissions - {username}
          </CardTitle>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : 'Save Permissions'}
          </Button>
        </div>

        <div className="table-responsive">
          <Table className="table-bordered">
            <thead>
              <tr>
                <th>Page</th>
                <th className="text-center" style={{ width: '100px' }}>
                  <div>All</div>
                  <input
                    type="checkbox"
                    checked={masterChecked}
                    onChange={(e) => handleMasterCheckboxChange(e.target.checked)}
                    className="master-checkbox"
                  />
                </th>
                <th className="text-center" style={{ width: '100px' }}>View</th>
                <th className="text-center" style={{ width: '100px' }}>Edit</th>
                <th className="text-center" style={{ width: '100px' }}>Delete</th>
                <th className="text-center" style={{ width: '100px' }}>Approve</th>
              </tr>
            </thead>
            <tbody>
              {renderPageRows(pages)}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default PermissionManager; 